/**
 * ModuleInfoModal.tsx — popup describing a module's implementation, downstream
 *                       effect, equations and validation evidence.
 *
 * Used everywhere a module is shown. Reads structured docs from `moduleDocs.ts`
 * and falls back to the registry's `longDesc` + `ref` for modules without
 * explicit docs (so the modal always opens with something useful).
 */

import { useState } from 'react';
import { Info, X, BookOpen, Sigma, ShieldCheck, Cog, Zap } from 'lucide-react';
import { MODULES } from '@/lib/moduleRegistry';
import { getModuleDoc } from '@/lib/moduleDocs';

interface Props {
  moduleId: string;
  /** Optional override for the trigger label. */
  triggerLabel?: string;
}

export default function ModuleInfoModal({ moduleId, triggerLabel = 'Implementation & Validation' }: Props) {
  const [open, setOpen] = useState(false);
  const mod = MODULES.find(m => m.id === moduleId);
  const doc = getModuleDoc(moduleId);

  if (!mod) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-700 bg-sky-900/30 px-2.5 py-1.5 text-xs font-semibold text-sky-200 hover:bg-sky-900/50"
        title="Open implementation & validation details"
      >
        <Info className="w-3.5 h-3.5" /> {triggerLabel}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-700 bg-slate-900 px-5 py-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-sky-300 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" /> Module Documentation
                </div>
                <div className="text-lg font-bold text-slate-100 mt-0.5">{mod.label}</div>
                <div className="text-xs text-slate-400">{mod.shortDesc}</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-5 text-sm">
              {/* Implementation */}
              <section>
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-emerald-200 mb-1.5">
                  <Cog className="w-4 h-4" /> Implementation
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {doc?.implementation ?? mod.longDesc}
                </p>
              </section>

              {/* Effect when enabled */}
              <section>
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-amber-200 mb-1.5">
                  <Zap className="w-4 h-4" /> Effect when ENABLED
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {doc?.effectWhenEnabled ??
                    `Modifies: ${mod.affects.join('; ')}.`}
                </p>
              </section>

              {/* Equations */}
              {doc?.equations && doc.equations.length > 0 && (
                <section>
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold text-sky-200 mb-2">
                    <Sigma className="w-4 h-4" /> Equations
                  </h3>
                  <ol className="space-y-2.5">
                    {doc.equations.map((eq, i) => (
                      <li key={i} className="rounded-lg border border-slate-700 bg-slate-950/50 p-3">
                        <code className="block font-mono text-sm text-sky-200 whitespace-pre-wrap break-words">
                          {eq.formula}
                        </code>
                        <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{eq.explain}</div>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Validation */}
              <section>
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-fuchsia-200 mb-1.5">
                  <ShieldCheck className="w-4 h-4" /> Validation
                </h3>
                {doc?.validation ? (
                  <div className="space-y-1.5">
                    <div className="text-slate-200">
                      <span className="text-xs uppercase tracking-wider text-slate-500">Source:&nbsp;</span>
                      <span className="text-sm">{doc.validation.source}</span>
                    </div>
                    {doc.validation.skill && (
                      <div className="text-slate-200">
                        <span className="text-xs uppercase tracking-wider text-slate-500">Skill:&nbsp;</span>
                        <span className="text-sm">{doc.validation.skill}</span>
                      </div>
                    )}
                    {doc.validation.range && (
                      <div className="text-slate-200">
                        <span className="text-xs uppercase tracking-wider text-slate-500">Range:&nbsp;</span>
                        <span className="text-sm">{doc.validation.range}</span>
                      </div>
                    )}
                    <div className="text-xs italic text-slate-400 mt-2 leading-relaxed">{doc.validation.notes}</div>
                  </div>
                ) : (
                  <p className="text-slate-300 leading-relaxed italic">
                    Reference: {mod.ref}
                  </p>
                )}
              </section>

              {/* Conflicts overview */}
              {((mod.hardConflicts?.length ?? 0) + (mod.softConflicts?.length ?? 0)) > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-slate-200 mb-1.5">Compatibility</h3>
                  {mod.hardConflicts?.map((c, i) => (
                    <div key={`h${i}`} className="text-xs text-red-300 mt-1">
                      <strong>Hard conflict</strong> with <code>{c.id}</code> — {c.reason}
                    </div>
                  ))}
                  {mod.softConflicts?.map((c, i) => (
                    <div key={`s${i}`} className="text-xs text-amber-300 mt-1">
                      <strong>Soft conflict</strong> with <code>{c.id}</code> — {c.reason}
                    </div>
                  ))}
                  {(!mod.hardConflicts || mod.hardConflicts.length === 0) &&
                    (!mod.softConflicts || mod.softConflicts.length === 0) && (
                      <div className="text-xs text-emerald-300">No conflicts with other modules — orthogonal physics.</div>
                    )}
                </section>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 border-t border-slate-700 bg-slate-900 px-5 py-3 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Module ID: <code className="text-slate-300">{mod.id}</code> · Path: <code className="text-slate-300">{mod.path}</code>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
